import classNames from 'classnames';
import $ from 'jquery';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation, withTranslation } from 'react-i18next';
import axios from '../../../utils/axiosConfig';
import i18n from '../../../utils/i18n';
import Button from '../Button';
import './Dropzone.scss';
import { AGENTS_ENDPOINTS } from '../../../config/api'
import { M_ENDPOINTS, UTIL_ENDPOINTS } from '../../../utils/constants'

const currentTranslationScope = Object.keys(CURRENT_TRANSLATIONS.en)[0]

/**
 * Dropzone type of event when uploading
 * @readonly
 * @enum {string}
 */
export const DROPZONE_STATE = {
  PROGRESS: 'PROGRESS',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
};

const initialState = {
  success: false,
  uploading: false,
  progress: 0,
  fileUrl: null,
  fileUrls: []
};

const Dropzone = ({
  onChange,
  selectFiles,
  successText,
  uploadingText,
  title,
  className,
  accept,
  disabled,
  name,
  multiple
}) => {
  const [state, setState] = useState({ ...initialState });
  const timer = useRef(null);
  const { t } = useTranslation();

  useEffect(() => () => clearTimeout(timer.current), []);

  const resetComponent = useCallback(() => {
    timer.current = setTimeout(() => {
      setState({
        ...initialState
      });
    }, 3000);
  }, [timer, state]);

  const getS3Path = () => {
    switch (window.location.pathname.split('/')[1]) {
      case 'clients':
        return AGENTS_ENDPOINTS.S3.POST;
      case 'm':
        return M_ENDPOINTS.S3.POST;
      default:
        return UTIL_ENDPOINTS.S3.POST;
    }
  };

  const onDrop = useCallback(acceptedFiles => {
    setState({
      progress: 0,
      success: false,
      error: false,
      uploading: true,
      fileUrl: null,
      fileUrls: []
    });

    const requests = [];

    for (let index = 0; index < acceptedFiles.length; index++) {
      const file = acceptedFiles[index];

      const formData = new FormData();
      formData.append("file", new Blob([file], { type: file.type }), file.name);
      formData.set("filetype", file.type);

      const token = $('meta[name="csrf-token"]').attr('content');

      const config = {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          const newState = {
            ...state,
            uploading: true,
            progress: percentCompleted,
          };
          setState(newState);
          onChange(DROPZONE_STATE.PROGRESS, { ...newState, file });
        },
        headers: {
          'X-CSRF-Token': token
        }
      };

      const url = getS3Path();

      requests.push(axios.post(url, formData, config))
    }

    axios.all(requests)
      .then((responses) => {
        const uploadedFiles = [];
        responses.forEach(({ data }) => {
          if (data.success) {
            let fileUrl = data.url;
            fileUrl = encodeURIComponent(
              decodeURIComponent(
                fileUrl.replace(/\+/g, '%20')
              ).replace(/^.*?:\/\//, '//')
            ).replace(/%2F/g, '/');

            uploadedFiles.push(fileUrl);
          }
        });

        if (uploadedFiles.length > 0) {
          let spreadState = {
            fileUrls: uploadedFiles,
          };

          if (!multiple) {
            spreadState = {
              fileUrl: uploadedFiles[0],
            };
          }
          const newState = {
            progress: 100,
            success: true,
            error: false,
            uploading: false,
            ...spreadState
          };
          setState(newState);
          onChange(DROPZONE_STATE.SUCCESS, {
            ...newState,
            file: multiple ? acceptedFiles : acceptedFiles[0]
          });
          return resetComponent();
        }
        throw new Error('Unknown error');
      }).catch((error) => {
        const newState = {
          progress: 0,
          success: false,
          error: true,
          uploading: false,
          fileUrl: null
        };
        setState(newState);
        onChange(DROPZONE_STATE.ERROR, { ...newState, ...error });
    });

  }, [state, resetComponent]);

  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept, disabled });

  const { uploading, success, error } = state;

  return (
    <div className={classNames("file-picker", className, { uploading, disabled })} {...getRootProps()}>
      <div className={classNames("dropzone-wrapper", { uploading, disabled, error })}>
        {success && !uploading ? <i className="fal fa-check-circle success" /> : null}
        {!success && uploading ? <i className="fal fa-spinner animate" /> : null}
        {!success && !uploading ? <i className="fal fa-upload" /> : null}

        {success && !uploading ? <div className="text">{successText}</div> : null}
        {!success && uploading ? <div className="text">
          {uploadingText}
        </div> : null}
        {!success && !uploading ? <div className="text">
          {title}
          {title && selectFiles && `\u00A0${t(`${currentTranslationScope}.common.or`)}`}
          {selectFiles ?
            <React.Fragment>
              <div className="mt-3">
                <Button variant="secondary" disabled={disabled}>
                  {t(`${currentTranslationScope}.common.select_file`)}
                </Button>
              </div>
            </React.Fragment>
            : null}
        </div> : null
        }
        <input name={name} accept={accept} {...getInputProps()} />
      </div>

    </div>
  );
};

Dropzone.propTypes = {
  accept: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  selectFiles: PropTypes.bool,
  successText: PropTypes.string,
  uploadingText: PropTypes.string,
  title: PropTypes.string,
  multiple: PropTypes.bool
};

Dropzone.defaultProps = {
  accept: '',
  successText: i18n.t(`${currentTranslationScope}.common.success`),
  uploadingText: i18n.t(`${currentTranslationScope}.common.uploading`),
  title: i18n.t(`${currentTranslationScope}.common.drag_n_drop_your_files_here`),
  multiple: false
};

export default withTranslation()(React.memo(Dropzone));
